import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Gear, Users, Buildings, Cloud, Shield, ClockCounterClockwise,
  Plus, FloppyDisk, X, CheckCircle,
} from "@phosphor-icons/react";

const TABS = [
  { key: "users", label: "User Management", icon: Users },
  { key: "tenants", label: "Tenants", icon: Buildings },
  { key: "api", label: "API Configuration", icon: Cloud },
  { key: "access", label: "Access Control", icon: Shield },
  { key: "audit", label: "Audit Log", icon: ClockCounterClockwise },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");

  if (user?.role !== "admin") {
    return (
      <div className="card p-10 text-center">
        <Shield size={36} className="mx-auto text-amber-500" weight="fill" />
        <h2 className="font-display text-xl font-bold mt-3">Admin access required</h2>
        <p className="text-sm text-slate-500 mt-1">
          Your account ({user?.email}) has role <span className="font-mono">{user?.role}</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-settings">
      <div>
        <div className="label-eyebrow">Administration</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Gear size={26} weight="fill" className="text-[#002FA7]" /> Admin Settings
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Manage users, tenants, API configuration, access control, and the audit trail.</p>
      </div>
      <div className="card overflow-hidden">
        <div className="tabs-bar px-4 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`admin-tab-${t.key}`}
                className={`tab-btn flex items-center gap-2 ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}>
                <Icon size={14} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-6">
          {tab === "users" && <UsersTab />}
          {tab === "tenants" && <TenantsTab />}
          {tab === "api" && <ApiTab />}
          {tab === "access" && <AccessTab />}
          {tab === "audit" && <AuditTab />}
        </div>
      </div>
    </div>
  );
}

/* ---------- USERS ---------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    const [u, t] = await Promise.all([http.get("/admin/users"), http.get("/admin/tenants")]);
    setUsers(u.data);
    setTenants(t.data);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-lg font-bold">Users</h2>
        <button data-testid="new-user-btn" className="btn btn-primary" onClick={() => setOpen(true)}>
          <Plus size={14} weight="bold" /> New User
        </button>
      </div>
      <table className="data-table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Tenant</th><th>Active</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td className="font-medium">{u.name}</td>
              <td className="font-mono text-xs">{u.email}</td>
              <td><span className={`pill ${u.role === "admin" ? "pill-offer" : "pill-new"}`}>{u.role}</span></td>
              <td>{u.tenantName}</td>
              <td>{u.active === false ? <span className="text-amber-600">disabled</span> : <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Active</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <NewUserModal tenants={tenants} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewUserModal({ tenants, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user", tenantId: tenants[0]?.id || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await http.post("/admin/users", form);
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="new-user-modal">
      <div className="card w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Create User</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          {["name", "email", "password"].map((f) => (
            <div key={f}>
              <label className="label-eyebrow block mb-1">{f}</label>
              <input data-testid={`nu-${f}`} type={f === "password" ? "password" : "text"} className="input" value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
            </div>
          ))}
          <div>
            <label className="label-eyebrow block mb-1">Role</label>
            <select data-testid="nu-role" className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Tenant</label>
            <select data-testid="nu-tenant" className="input" value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })}>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {err && <div className="text-xs text-red-700">{String(err)}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button data-testid="nu-save" className="btn btn-primary" onClick={save} disabled={busy || !form.email || !form.password || !form.name}>
            {busy ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- TENANTS ---------- */
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [reveal, setReveal] = useState({});
  const load = async () => setTenants((await http.get("/admin/tenants")).data);
  useEffect(() => { load(); }, []);
  const regen = async (id) => {
    if (!window.confirm("Regenerate access key? All users of this tenant will need the new key to log in.")) return;
    await http.post(`/admin/tenants/${id}/regenerate-key`);
    await load();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-lg font-bold">Tenants</h2>
        <button data-testid="new-tenant-btn" className="btn btn-primary" onClick={() => setOpen(true)}>
          <Plus size={14} weight="bold" /> New Tenant
        </button>
      </div>
      <table className="data-table">
        <thead><tr><th>Name</th><th>Access Key</th><th>Created</th><th></th></tr></thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.id}>
              <td className="font-medium">{t.name}</td>
              <td>
                <span data-testid={`tenant-key-${t.id}`} className="font-mono text-xs">
                  {reveal[t.id] ? t.accessKey : "••••••••••••••••"}
                </span>
                <button className="ml-2 text-[11px] text-[#002FA7] underline" onClick={() => setReveal((r) => ({ ...r, [t.id]: !r[t.id] }))}>
                  {reveal[t.id] ? "hide" : "show"}
                </button>
              </td>
              <td className="font-mono text-[11px]">{t.createdAt?.slice(0, 10)}</td>
              <td>
                <button data-testid={`regen-key-${t.id}`} className="btn btn-outline !py-1 !px-2 !text-[11px]" onClick={() => regen(t.id)}>
                  Regenerate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <NewTenantModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post("/admin/tenants", form);
      onCreated();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40">
      <div className="card w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Create Tenant</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Name</label>
          <input
            data-testid="nt-name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button data-testid="nt-save" className="btn btn-primary" onClick={save} disabled={busy || !form.name}>{busy ? "…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- API CONFIG ---------- */
function ApiTab() {
  const [tenants, setTenants] = useState([]);
  const [selId, setSelId] = useState("");
  const [form, setForm] = useState({ fusionUrl: "", fusionUsername: "", fusionPassword: "" });
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [teamsBusy, setTeamsBusy] = useState(false);
  const [teamsTest, setTeamsTest] = useState(null);

  useEffect(() => { http.get("/admin/tenants").then((r) => { setTenants(r.data); if (r.data[0]) { setSelId(r.data[0].id); setForm(r.data[0]); } }); }, []);
  useEffect(() => {
    const t = tenants.find((x) => x.id === selId);
    if (t) setForm({ ...t, fusionPassword: "" });
  }, [selId, tenants]);

  const save = async () => {
    const body = { name: form.name, fusionUrl: form.fusionUrl, fusionUsername: form.fusionUsername };
    if (form.fusionPassword) body.fusionPassword = form.fusionPassword;
    // Teams config — send only when the fields have content so we don't blank out saved values.
    if (form.teamsClientId !== undefined) body.teamsClientId = form.teamsClientId || "";
    if (form.teamsTenantId !== undefined) body.teamsTenantId = form.teamsTenantId || "";
    if (form.teamsOrganizerId !== undefined) body.teamsOrganizerId = form.teamsOrganizerId || "";
    if (form.teamsClientSecret) body.teamsClientSecret = form.teamsClientSecret;
    await http.patch(`/admin/tenants/${selId}`, body);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const testTeams = async () => {
    setTeamsTest(null); setTeamsBusy(true);
    try {
      const r = await http.post(`/admin/tenants/${selId}/teams/test`);
      setTeamsTest(r.data);
    } catch (e) {
      setTeamsTest({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setTeamsBusy(false); }
  };

  const testConnection = async () => {
    // Fusion probe intentionally removed — Vaku runs locally.
    setTestError(null);
    setTestResult(null);
  };

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-bold">API Configuration (per Tenant)</h2>
      <p className="text-sm text-slate-500">Set the integrations for each tenant. LLM key & Email domain are managed under their dedicated screens.</p>
      <div>
        <label className="label-eyebrow block mb-1">Tenant</label>
        <select data-testid="api-tenant" className="input max-w-xs" value={selId} onChange={(e) => setSelId(e.target.value)}>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <EmailDiagnostics />

      {/* -------- Microsoft Teams -------- */}
      <div className="rounded-md border border-slate-200 bg-white p-4 max-w-3xl" data-testid="teams-settings">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="font-display text-base font-bold">Microsoft Teams</div>
            <div className="text-xs text-slate-500">
              Auto-create Teams meetings for AI Interviews and pull the transcript back for AI scoring.
              Requires an Azure AD app registration in your Microsoft 365 tenant with application permissions
              <span className="font-mono"> OnlineMeetings.ReadWrite.All</span> +
              <span className="font-mono"> OnlineMeetingTranscript.Read.All</span>.
            </div>
          </div>
          <a
            href="https://learn.microsoft.com/en-us/graph/cloud-communication-online-meeting-application-access-policy"
            target="_blank" rel="noopener noreferrer"
            className="text-xs text-[#002FA7] hover:underline whitespace-nowrap"
          >Setup guide ↗</a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field
            label="Application (Client) ID"
            v={form.teamsClientId || ""}
            onChange={(v) => setForm({ ...form, teamsClientId: v })}
            testid="teams-client-id"
          />
          <Field
            label="Directory (Tenant) ID"
            v={form.teamsTenantId || ""}
            onChange={(v) => setForm({ ...form, teamsTenantId: v })}
            testid="teams-tenant-id"
          />
          <Field
            label="Organizer User Object ID"
            v={form.teamsOrganizerId || ""}
            onChange={(v) => setForm({ ...form, teamsOrganizerId: v })}
            testid="teams-organizer-id"
          />
          <Field
            label="Client Secret (leave blank to keep)"
            type="password"
            v={form.teamsClientSecret || ""}
            onChange={(v) => setForm({ ...form, teamsClientSecret: v })}
            testid="teams-client-secret"
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            data-testid="teams-test"
            className="btn btn-outline"
            onClick={testTeams}
            disabled={teamsBusy || !form.teamsClientId}
          >
            {teamsBusy ? "Testing…" : "Test Teams connection"}
          </button>
        </div>
        {teamsTest && (
          <div
            className={`mt-3 rounded-md border p-3 text-xs ${teamsTest.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}
            data-testid="teams-test-result"
          >
            {teamsTest.ok ? (
              <>
                <strong>Teams connected.</strong> Token acquired ✓
                {teamsTest.license === true && " · Transcript license detected ✓"}
                {teamsTest.license === false && " · No transcript-capable license — meeting will still be created; use upload transcript fallback."}
                {teamsTest.license === null && " · License check inconclusive — meeting will still be created."}
              </>
            ) : (
              <><strong>Test failed:</strong> {teamsTest.error || teamsTest.reason || teamsTest.detail || "Unknown error"}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- EMAIL DELIVERY DIAGNOSTICS ---------- */
function EmailDiagnostics() {
  const [health, setHealth] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    http.get("/email/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  const sendTest = async () => {
    setTesting(true); setError(null); setResult(null);
    try {
      const { data } = await http.post("/email/test");
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 max-w-3xl mt-6" data-testid="email-diagnostics">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-base font-bold">Email Delivery (Resend)</div>
          <div className="text-xs text-slate-500 mt-0.5">Verify transactional email is wired on this environment.</div>
        </div>
        <button
          data-testid="email-test-btn"
          className="btn btn-outline"
          onClick={sendTest}
          disabled={testing || !health?.configured}
        >
          {testing ? "Sending…" : "Send test email to me"}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">API key</div>
          <div className="font-mono text-xs">
            {health?.configured
              ? <span className="text-emerald-700">✓ RESEND_API_KEY set</span>
              : <span className="text-red-700">✗ missing</span>}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">Sender</div>
          <div className="font-mono text-xs">{health?.sender || "—"}</div>
        </div>
      </div>

      {health?.hint && (
        <div className="mt-3 text-xs text-slate-600 leading-relaxed border-l-2 border-slate-200 pl-3">{health.hint}</div>
      )}

      {result && result.sent && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="email-test-ok">
          ✅ Test email sent (id <code className="text-xs">{result.id}</code>). Check your inbox <strong>and Spam folder</strong>.
        </div>
      )}
      {result && !result.sent && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="email-test-fail">
          <strong>Resend rejected the send:</strong> {result.error}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-4 text-xs text-slate-500 leading-relaxed">
        <strong>Why emails might not arrive:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li><strong>Spam folder</strong> — <code>onboarding@resend.dev</code> is Resend&apos;s shared sandbox sender and is aggressively filtered by Gmail/Outlook. Always check Spam first.</li>
          <li><strong>Sandbox limits</strong> — On the Resend free tier you can only send to the email that owns the Resend account. To email real candidates, verify a domain at <a className="text-primary underline" target="_blank" rel="noreferrer" href="https://resend.com/domains">resend.com/domains</a> and update <code>SENDER_EMAIL</code> to use it.</li>
          <li><strong>Production env vars</strong> — Preview and Production are separate. If this page says &quot;missing&quot; on production, add <code>RESEND_API_KEY</code> in your deployment env and redeploy.</li>
        </ul>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input data-testid={testid} type={type} className="input" value={v || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/* ---------- ACCESS CONTROL (read-only matrix for now) ---------- */
function AccessTab() {
  const matrix = [
    { module: "Candidates / Screening", user: "View, Add, Edit, Screen", admin: "Full" },
    { module: "Job Requisitions", user: "View, AI Tools", admin: "Full" },
    { module: "Communications", user: "View, Send Email", admin: "Full + Bulk Process" },
    { module: "Insights", user: "View", admin: "View" },
    { module: "AI Video Interviews", user: "—", admin: "Full" },
    { module: "Admin Settings", user: "—", admin: "Full" },
  ];
  return (
    <div className="space-y-3">
      <h2 className="font-display text-lg font-bold">Access Control Matrix</h2>
      <p className="text-sm text-slate-500">Default capabilities per role. Per-permission tuning is on the roadmap.</p>
      <table className="data-table">
        <thead><tr><th>Module</th><th>user</th><th>admin</th></tr></thead>
        <tbody>
          {matrix.map((m) => (
            <tr key={m.module}><td className="font-medium">{m.module}</td><td>{m.user}</td><td>{m.admin}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- AUDIT LOG ---------- */
function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { http.get("/admin/audit-log").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-3">
      <h2 className="font-display text-lg font-bold">Audit Log</h2>
      <table className="data-table">
        <thead><tr><th>Time</th><th>Actor</th><th>Tenant</th><th>Action</th><th>Target</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-mono text-[11px]">{new Date(r.ts).toLocaleString()}</td>
              <td>{r.actorEmail}</td>
              <td className="font-mono text-[11px]">{r.tenantId}</td>
              <td>{r.action}</td>
              <td className="text-slate-600">{r.target || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-8">No audit events yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
